/**
 * Socket.io Client for Podcast Manager
 *
 * This module provides WebSocket connectivity for real-time episode updates.
 * The podcast manager manages its own WebSocket connection to the API server.
 */

import { io, Socket } from 'socket.io-client';
import { getApiBaseUrl } from './api';
// Import Firebase auth from parent app for token retrieval
// @ts-ignore - Module Federation dynamic import
import { auth } from 'shell/Firebase';
import type {
  PresenceUpdate,
  PresenceJoinPayload,
  PresenceLeavePayload,
  PresenceHeartbeatPayload,
} from '@/types/presence.types';

// Derive WebSocket URL from API base URL
// API base: http://localhost:3010/api/v1 -> WS: http://localhost:3010
// API base: https://api.mediaorchestrator.com/api/v1 -> WS: https://api.mediaorchestrator.com
const getWebSocketUrl = (): string => {
  const apiUrl = getApiBaseUrl();

  try {
    const url = new URL(apiUrl);
    const host = url.host;

    // Use the same host as the API URL (could be localhost, homelab IP, or production domain)
    const wsUrl = `${url.protocol}//${host}`;
    return wsUrl;
  } catch {
    // Fallback for development (home lab)
    return 'http://192.168.1.75:3010';
  }
};

// Socket instance (singleton for podcast-manager)
let socket: Socket | null = null;
let connectionPromise: Promise<Socket> | null = null;

// Event types for real-time updates
export interface EpisodeUpdate {
  id: string;
  companyId: string;
  status: string;
  title?: string;
  updatedAt: string;
  updatedBy?: string;
  [key: string]: unknown;
}

/**
 * Initialize and connect to WebSocket server
 */
export const connectSocket = async (): Promise<Socket> => {
  const wsUrl = getWebSocketUrl();

  // Return existing connection if available
  if (socket?.connected) {
    return socket;
  }

  // Return pending connection if in progress
  if (connectionPromise) {
    return connectionPromise;
  }

  connectionPromise = new Promise(async (resolve, reject) => {
    try {
      // Get Firebase token for authentication
      const user = auth.currentUser;
      const token = user ? await user.getIdToken() : null;

      socket = io(wsUrl, {
        auth: {
          token,
        },
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        timeout: 20000,
      });

      socket.on('connect', () => {
        resolve(socket!);
      });

      socket.on('connect_error', (error) => {
        console.error('[Podcast WS] Connection error:', error.message);
        connectionPromise = null;
        reject(error);
      });

      socket.on('disconnect', () => {
        connectionPromise = null;
      });

      socket.on('reconnect', () => {
        // Reconnected successfully
      });

      socket.on('error', (error) => {
        console.error('[Podcast WS] Error:', error);
      });
    } catch (error) {
      console.error('[Podcast WS] Failed to connect:', error);
      connectionPromise = null;
      reject(error);
    }
  });

  return connectionPromise;
};

/**
 * Disconnect from WebSocket server
 */
export const disconnectSocket = (): void => {
  if (socket) {
    socket.disconnect();
    socket = null;
    connectionPromise = null;
  }
};

/**
 * Get current socket instance
 */
export const getSocket = (): Socket | null => socket;

/**
 * Check if socket is connected
 */
export const isConnected = (): boolean => socket?.connected ?? false;

/**
 * Subscribe to a specific episode's updates
 */
export const subscribeToEpisode = async (episodeId: string): Promise<void> => {
  const sock = await connectSocket();
  sock.emit('subscribe:episode', episodeId);
};

/**
 * Unsubscribe from a specific episode's updates
 */
export const unsubscribeFromEpisode = async (episodeId: string): Promise<void> => {
  if (!socket?.connected) return;

  socket.emit('unsubscribe:episode', episodeId);
};

/**
 * Subscribe to all episodes for the current company
 */
export const subscribeToEpisodes = async (): Promise<void> => {
  const sock = await connectSocket();
  sock.emit('subscribe:episodes');
};

/**
 * Unsubscribe from all episodes
 */
export const unsubscribeFromEpisodes = async (): Promise<void> => {
  if (!socket?.connected) return;

  socket.emit('unsubscribe:episodes');
};

/**
 * Listen for episode update events
 */
export const onEpisodeUpdate = (callback: (data: EpisodeUpdate) => void): (() => void) => {
  if (!socket) {
    return () => {};
  }

  socket.on('episode:updated', callback);

  // Return cleanup function
  return () => {
    socket?.off('episode:updated', callback);
  };
};

/**
 * Event type for podcast metrics updates
 */
export interface PodcastMetricsEvent {
  companyId: string;
  metrics: {
    needsReview: number;
    inProgress: number;
    readyToPublish: number;
    published: number;
    failed: number;
  };
  timestamp: string;
}

/**
 * Listen for podcast metrics update events
 * These are emitted by the API when episode statuses change
 */
export const onPodcastMetricsUpdate = (callback: (data: PodcastMetricsEvent) => void): (() => void) => {
  if (!socket) {
    return () => {};
  }

  socket.on('podcast:metrics:updated', callback);

  // Return cleanup function
  return () => {
    socket?.off('podcast:metrics:updated', callback);
  };
};

// ===== PRESENCE FUNCTIONS =====

/**
 * Join presence for an episode (announce that user is viewing)
 */
export const joinPresence = async (
  episodeId: string,
  userData: { displayName: string; photoURL: string | null; email: string | null }
): Promise<void> => {
  const sock = await connectSocket();
  const payload: PresenceJoinPayload = {
    episodeId,
    displayName: userData.displayName,
    photoURL: userData.photoURL,
    email: userData.email,
  };
  sock.emit('presence:join', payload);
};

/**
 * Leave presence for an episode
 */
export const leavePresence = async (episodeId: string): Promise<void> => {
  if (!socket?.connected) return;

  const payload: PresenceLeavePayload = { episodeId };
  socket.emit('presence:leave', payload);
};

/**
 * Send heartbeat to keep presence alive
 */
export const sendPresenceHeartbeat = async (episodeId: string): Promise<void> => {
  if (!socket?.connected) return;

  const payload: PresenceHeartbeatPayload = { episodeId };
  socket.emit('presence:heartbeat', payload);
};

/**
 * Listen for presence updates
 */
export const onPresenceUpdate = (callback: (data: PresenceUpdate) => void): (() => void) => {
  if (!socket) {
    return () => {};
  }

  socket.on('presence:update', callback);

  // Return cleanup function
  return () => {
    socket?.off('presence:update', callback);
  };
};

const socketService = {
  connect: connectSocket,
  disconnect: disconnectSocket,
  getSocket,
  isConnected,
  subscribeToEpisode,
  unsubscribeFromEpisode,
  subscribeToEpisodes,
  unsubscribeFromEpisodes,
  onEpisodeUpdate,
  onPodcastMetricsUpdate,
  // Presence functions
  joinPresence,
  leavePresence,
  sendPresenceHeartbeat,
  onPresenceUpdate,
};

export default socketService;
